import { captureError } from "@/lib/sentry";
import { supabase } from "@/integrations/supabase/client";

/**
 * Catch the blank screen, and say enough about it to name the cause.
 *
 * VA-230, Bridget: "I'm still seeing a blank screen on the PWA in the web version of the
 * app almost daily. I have to clear my cookies and caches or … I just log in with
 * incognito. We really need to get to the root cause."
 *
 * One real cause was found and fixed on 13 Aug (8ce9d58): supabase-js serialises token
 * work behind a Web Lock shared by every tab on the origin, so one window that stopped
 * pumping its event loop starved the rest — reproduced as a render of zero characters
 * that never recovered. She is still reporting it, which means either something else
 * does it too, or she is still running the build from before that fix.
 *
 * Those two need opposite responses — more code versus better delivery — and nothing
 * visible from here tells them apart. Three guesses have already gone into this class of
 * bug, so this reports rather than guesses again.
 *
 * Deliberately NOT a fix, and deliberately not a reload: a reload would destroy the
 * evidence and turn a reproducible fault into an intermittent one.
 */

const GRACE_MS = 8000;

/** Which auth locks are held, and by whom. Names the 13 Aug cause if it recurs. */
async function heldLocks(): Promise<string[]> {
  try {
    const q = (navigator as Navigator & { locks?: LockManager }).locks;
    if (!q?.query) return ["locks-api-unavailable"];
    const state = await q.query();
    return (state.held ?? [])
      .map((l) => l.name ?? "")
      .filter((n) => n.includes("auth-token"));
  } catch {
    return ["locks-query-threw"];
  }
}

/**
 * The bundle actually executing, versus the one index.html asked for.
 *
 * A mismatch is the whole "is she on a stale build" question answered in one field: the
 * service worker can hand back a precached module while the freshly fetched HTML points
 * at a newer hash.
 */
function bundleState(): { requested: string[]; executing: string | null } {
  const requested = [...document.querySelectorAll<HTMLScriptElement>('script[src*="/assets/"]')]
    .map((s) => s.src.split("/").pop() ?? "")
    .filter(Boolean);
  let executing: string | null = null;
  try {
    // The stack of an Error thrown here names the module it was thrown from.
    const m = new Error().stack?.match(/assets\/([A-Za-z0-9._-]+\.js)/);
    executing = m?.[1] ?? null;
  } catch { /* stack formats vary; absence is not itself a signal */ }
  return { requested, executing };
}

function storedSession(): { present: boolean; expired: boolean | null } {
  try {
    const key = Object.keys(localStorage).find((k) => /^sb-.*-auth-token$/.test(k));
    if (!key) return { present: false, expired: null };
    const raw = localStorage.getItem(key);
    if (!raw) return { present: false, expired: null };
    const parsed = JSON.parse(raw);
    const exp = parsed?.expires_at;
    return { present: true, expired: typeof exp === "number" ? exp * 1000 <= Date.now() : null };
  } catch {
    return { present: false, expired: null };
  }
}

export function watchForBlankScreen(rootId = "root"): void {
  if (typeof window === "undefined") return;

  window.setTimeout(async () => {
    const root = document.getElementById(rootId);
    // "Blank" means React never put anything in the tree. A spinner counts as rendered:
    // this is looking for nothing at all, which is what Bridget describes and what the
    // 13 Aug repro produced.
    if (!root || root.childElementCount > 0) return;

    const sw = navigator.serviceWorker;
    const bundle = bundleState();

    const facts = {
      va230: true,
      afterMs: GRACE_MS,
      route: window.location.pathname + window.location.search,
      online: navigator.onLine,
      display: window.matchMedia?.("(display-mode: standalone)")?.matches ? "pwa" : "browser",
      // Stale build? These two disagreeing is the answer.
      bundleRequested: bundle.requested,
      bundleExecuting: bundle.executing,
      // Which service worker is in charge, if any.
      swControlled: !!sw?.controller,
      swScript: sw?.controller?.scriptURL ?? null,
      swState: sw?.controller?.state ?? null,
      // The 13 Aug cause, recurring.
      heldAuthLocks: await heldLocks(),
      // Signed in but stuck, versus never signed in — different bugs entirely.
      session: storedSession(),
      userAgent: navigator.userAgent,
    };

    captureError(new Error("Blank screen: root has no children after load"), facts);

    // VA-270: the same facts somewhere we can actually read.
    //
    // This has reported to Sentry since 15 Aug and in ten days nobody has read one —
    // there is no Sentry token on the machine the work happens on, and the Sentry→Jira
    // integration never raised an issue for it either. An instrument nobody can query is
    // the same as no instrument, which is how this bug survived three fixes.
    //
    // error_logs is queryable with the service role key, and staleClientWatch already
    // reports through this route. Best-effort and last: Sentry has the event either way,
    // and a diagnostic must never be the thing that breaks the page it is diagnosing.
    try {
      await supabase.functions.invoke("log-error", {
        body: {
          category: "browser_compat",
          action: "blank-screen",
          message: [
            "BLANK SCREEN — root had no children after load.",
            `route: ${facts.route}`,
            `display: ${facts.display}  online: ${facts.online}`,
            `bundleRequested: ${facts.bundleRequested.join(", ") || "none"}`,
            `bundleExecuting: ${facts.bundleExecuting ?? "unknown"}`,
            `swControlled: ${facts.swControlled}  swState: ${facts.swState ?? "none"}`,
            `swScript: ${facts.swScript ?? "none"}`,
            `heldAuthLocks: ${facts.heldAuthLocks.join(", ") || "none"}`,
            `session present: ${facts.session.present}  expired: ${facts.session.expired}`,
          ].join("\n"),
          status: null,
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
      });
    } catch { /* the Sentry report above already went out */ }
  }, GRACE_MS);
}
